import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaBoxOpen } from 'react-icons/fa';
import { formatCurrency } from '../../utils/currency';
import { useSettings } from '../../context/SettingsContext';
import { useToast } from '../../context/ToastContext';
import { LoadingState } from '../../components/Loading';
import Modal from '../../components/Modal';

const AdminProducts = () => {
  const { settings } = useSettings();
  const toast = useToast();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => { loadProducts(); }, []);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/products?limit=100&all=true');
      setProducts(res.data.products);
    } catch (e) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`/api/products/${deleteTarget._id}`);
      toast.success('Product deleted');
      setDeleteTarget(null);
      loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Delete failed');
    }
  };

  const filtered = products.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="admin-header">
        <div>
          <h1>Products</h1>
          <p className="admin-header-subtitle">{products.length} products in your catalog</p>
        </div>
        <Link to="/admin/products/new" className="btn btn-primary">
          <FaPlus /> Add product
        </Link>
      </div>

      <div className="admin-toolbar">
        <div className="search-wrapper">
          <FaSearch />
          <input
            type="search"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <LoadingState />
      ) : filtered.length === 0 ? (
        <div className="card empty-state">
          <FaBoxOpen className="empty-state-icon" />
          <h3>{search ? 'No products match your search' : 'No products yet'}</h3>
          <p>{search ? 'Try a different search term' : 'Add your first product to get started'}</p>
          {!search && (
            <Link to="/admin/products/new" className="btn btn-primary">
              <FaPlus /> Add product
            </Link>
          )}
        </div>
      ) : (
        <div className="admin-table">
          <div className="admin-table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Stock</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((product) => (
                  <tr key={product._id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {product.images?.[0] ? (
                          <img src={product.images[0].url} alt={product.name} className="product-thumb" />
                        ) : (
                          <span className="product-thumb-placeholder"><FaBoxOpen /></span>
                        )}
                        <div>
                          <div style={{ fontWeight: 600 }}>{product.name}</div>
                          {product.featured && (
                            <span style={{ fontSize: 11, color: 'var(--accent-600)', fontWeight: 600 }}>★ Featured</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={{ color: 'var(--gray-600)', fontSize: 13 }}>
                      {product.category?.name || '—'}
                    </td>
                    <td style={{ fontWeight: 600 }}>
                      {formatCurrency(product.price, settings?.currency)}
                    </td>
                    <td>
                      <span className={`badge ${product.stock > 10 ? 'success' : product.stock > 0 ? 'warning' : 'danger'}`}>
                        {product.stock} units
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${product.isActive ? 'success' : 'neutral'}`}>
                        {product.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-primary"
                          onClick={() => navigate(`/admin/products/edit/${product._id}`)}
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="btn-icon btn-danger"
                          onClick={() => setDeleteTarget(product)}
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete product?"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>
              Cancel
            </button>
            <button className="btn btn-danger" onClick={handleDelete}>
              Delete
            </button>
          </>
        }
      >
        <p>
          Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?
          This action cannot be undone.
        </p>
      </Modal>
    </div>
  );
};

export default AdminProducts;
